/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, takeLeading, call, all } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  ActionFail,
  ActionRequest,
  ActionSuccess,
  HideUnsubscribed,
  LocalStorage,
  XError,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import AppConfig from 'services/AppConfig';

export enum ActionTypes {
  LOAD_SHOW_UNSUBSCRIBED_CHANNELS = 'contentSettings/LOAD_SHOW_UNSUBSCRIBED_CHANNELS',
  LOAD_SHOW_UNSUBSCRIBED_CHANNELS_SUCCESS = 'contentSettings/LOAD_SHOW_UNSUBSCRIBED_CHANNELS_SUCCESS',
  LOAD_SHOW_UNSUBSCRIBED_CHANNELS_FAIL = 'contentSettings/LOAD_SHOW_UNSUBSCRIBED_CHANNELS_FAIL',
  SET_SHOW_UNSUBSCRIBED_CHANNELS = 'contentSettings/SET_SHOW_UNSUBSCRIBED_CHANNELS',
  SET_SHOW_UNSUBSCRIBED_CHANNELS_SUCCESS = 'contentSettings/SET_SHOW_UNSUBSCRIBED_CHANNELS_SUCCESS',
  SET_SHOW_UNSUBSCRIBED_CHANNELS_FAIL = 'contentSettings/SET_SHOW_UNSUBSCRIBED_CHANNELS_FAIL',
}

interface ShowUnsubscribedChannelsPayload {
  showUnsubscribedChannelsValue: boolean;
}

type LoadShowUnsubscribedChannels = ActionRequest<ActionTypes.LOAD_SHOW_UNSUBSCRIBED_CHANNELS>;
type SetShowUnsubscribedChannels = ActionRequest<
  ActionTypes.SET_SHOW_UNSUBSCRIBED_CHANNELS,
  ShowUnsubscribedChannelsPayload
>;
type SetShowUnsubscribedChannelsSuccess = ActionSuccess<
  ActionTypes.SET_SHOW_UNSUBSCRIBED_CHANNELS_SUCCESS,
  ShowUnsubscribedChannelsPayload
>;
type SetShowUnsubscribedChannelsFail = ActionFail<ActionTypes.SET_SHOW_UNSUBSCRIBED_CHANNELS_FAIL>;
type LoadShowUnsubscribedChannelsSucess = ActionSuccess<
  ActionTypes.LOAD_SHOW_UNSUBSCRIBED_CHANNELS_SUCCESS,
  ShowUnsubscribedChannelsPayload
>;
type LoadShowUnsubscribedChannelsFailed = ActionFail<ActionTypes.LOAD_SHOW_UNSUBSCRIBED_CHANNELS_FAIL>;

type Actions =
  | LoadShowUnsubscribedChannels
  | LoadShowUnsubscribedChannelsSucess
  | LoadShowUnsubscribedChannelsFailed
  | SetShowUnsubscribedChannels
  | SetShowUnsubscribedChannelsSuccess
  | SetShowUnsubscribedChannelsFail;

const Actions = {
  loadShowUnsubscribedChannelsFailed: (error: XError): LoadShowUnsubscribedChannelsFailed => ({
    type: ActionTypes.LOAD_SHOW_UNSUBSCRIBED_CHANNELS_FAIL,
    error,
  }),
  loadShowUnsubscribedChannelsSucess: (showUnsubscribedChannelsValue: boolean): LoadShowUnsubscribedChannelsSucess => ({
    type: ActionTypes.LOAD_SHOW_UNSUBSCRIBED_CHANNELS_SUCCESS,
    payload: { showUnsubscribedChannelsValue },
  }),
  loadShowUnsubscribedChannels: (): LoadShowUnsubscribedChannels => ({
    type: ActionTypes.LOAD_SHOW_UNSUBSCRIBED_CHANNELS,
  }),
  setShowUnsubscribedChannels: (showUnsubscribedChannelsValue: boolean): SetShowUnsubscribedChannels => ({
    type: ActionTypes.SET_SHOW_UNSUBSCRIBED_CHANNELS,
    meta: { showUnsubscribedChannelsValue },
  }),
  setShowUnsubscribedChannelsSuccess: (showUnsubscribedChannelsValue: boolean): SetShowUnsubscribedChannelsSuccess => ({
    type: ActionTypes.SET_SHOW_UNSUBSCRIBED_CHANNELS_SUCCESS,
    payload: { showUnsubscribedChannelsValue },
  }),
  setShowUnsubscribedChannelsFail: (error: XError): SetShowUnsubscribedChannelsFail => ({
    type: ActionTypes.SET_SHOW_UNSUBSCRIBED_CHANNELS_FAIL,
    error,
  }),
};

export const ActionCreators = {
  loadShowUnsubscribedChannels: Actions.loadShowUnsubscribedChannels,
  setShowUnsubscribedChannels: Actions.setShowUnsubscribedChannels,
};

export interface ReducerState {
  showUnsubscribedChannels?: boolean;
  processing?: boolean;
  processed?: boolean;
  error?: XError;
}

// REDUCERS
const initialState: ReducerState = {
  processing: false,
  processed: false,
  error: undefined,
  showUnsubscribedChannels: true,
};

export default function parentalControl(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionTypes.SET_SHOW_UNSUBSCRIBED_CHANNELS:
    case ActionTypes.LOAD_SHOW_UNSUBSCRIBED_CHANNELS:
      return {
        ...state,
        processing: true,
        processed: false,
        error: undefined,
      };
    case ActionTypes.SET_SHOW_UNSUBSCRIBED_CHANNELS_SUCCESS:
    case ActionTypes.LOAD_SHOW_UNSUBSCRIBED_CHANNELS_SUCCESS:
      return {
        ...state,
        processing: false,
        processed: true,
        showUnsubscribedChannels: action.payload.showUnsubscribedChannelsValue,
      };
    case ActionTypes.SET_SHOW_UNSUBSCRIBED_CHANNELS_FAIL:
    case ActionTypes.LOAD_SHOW_UNSUBSCRIBED_CHANNELS_FAIL:
      return {
        ...state,
        processing: false,
        processed: true,
        error: action.error,
      };
    default:
      return state;
  }
}

// SAGAS
function* getShowUnsubscribedChannelsFromStorage(): SagaIterator<boolean | undefined> {
  return yield call(LocalStorage.getShowUnsubscribedChannels);
}

function* setShowUnsubscribedChannelsToStorage(showUnsubscribedChannelsValue: boolean): SagaIterator {
  yield call(LocalStorage.storeShowUnsubscribedChannels, showUnsubscribedChannelsValue);
}

function* loadShowUnsubscribedChannelsSaga() {
  try {
    const valueStored: boolean | undefined = yield call(getShowUnsubscribedChannelsFromStorage);

    const showUnsubscribedChannelsAsDefault =
      HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY] === HideUnsubscribed.SHOW ||
      HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY] === undefined;

    yield put(Actions.loadShowUnsubscribedChannelsSucess(valueStored ?? showUnsubscribedChannelsAsDefault));
  } catch (error) {
    yield put(Actions.loadShowUnsubscribedChannelsFailed(XError.fromError(error)));
  }
}

function* setShowUnsubscribedChannelsSaga(action: SetShowUnsubscribedChannels) {
  const { showUnsubscribedChannelsValue } = action.meta;
  try {
    yield call(setShowUnsubscribedChannelsToStorage, showUnsubscribedChannelsValue);
    yield put(Actions.setShowUnsubscribedChannelsSuccess(showUnsubscribedChannelsValue));
  } catch (error) {
    yield put(Actions.setShowUnsubscribedChannelsFail(XError.fromError(error)));
  }
}

export function* saga(): SagaIterator {
  yield all([
    takeLeading(ActionTypes.LOAD_SHOW_UNSUBSCRIBED_CHANNELS, loadShowUnsubscribedChannelsSaga),
    takeLeading(ActionTypes.SET_SHOW_UNSUBSCRIBED_CHANNELS, setShowUnsubscribedChannelsSaga),
  ]);
}

export const Selectors = {
  getShowUnsubscribedChannels: (state: StoreState): boolean | undefined =>
    state.contentSettings.showUnsubscribedChannels,
};
